import {
  ParameterManager,
  createEmptyMetadataIndex,
  paramValuesEqual,
  type ParamApplyProgress,
  type ParamApplyReport,
  type ParamDownloadPhase,
  type ParamDownloadResult,
  type ParamListItem,
  type ParamMetadataIndex,
  type ParamSetResult,
  type ParamSetStatus,
  type ParamValidationResult,
  type ParameterManagerOptions,
  type Unsubscribe,
  type Vehicle,
} from '@hgc/core';

import { dangerReasonOf, isDangerousParam } from './danger';
import { ParamMetadataLoader, type ParamMetadataState } from './metadata';

/**
 * 参数会话：一个机体 + 一个参数管理器 + 一份"待写入改动"的记账。
 *
 * 为什么要有这一层，而不是让 React 组件直接用 `ParameterManager`：
 *
 * 1. **写入的结论必须落到数据里，而不是散在组件的局部 state 里。** 一次 `PARAM_SET`
 *    的结果（成功 / 值不符 / 没回应）是"这个参数现在到底是多少"的一部分。放在组件里，
 *    一切换页签、重渲染一次就没了 —— 而失败恰恰是用户 10 秒后才抬头看屏幕时要知道的事。
 * 2. **待写入改动是跨页签的。** 参数页改了值、去摘要页看一眼、再回来，改动必须还在
 *    （否则用户会以为"我明明改过"）。
 * 3. **一个机体只需要一个管理器。** 每个组件各建一个，会让同一份参数表在链路上被下载
 *    N 次，而且每个管理器各持一份 `paramValue` 订阅。
 *
 * 快照是不可变的、引用稳定的（`useSyncExternalStore` 用 `Object.is` 判断变化），
 * 因此只在**真的有事发生**时重建一次。
 */

/** 参数行的显示上限。上千行全部进 DOM 会让低端设备在拖动时卡住；筛选/搜索是缩小的正道 */
export const PARAM_ROW_LIMIT = 400;

export interface PendingEdit {
  readonly name: string;
  /** 改动前的值（写回确认、失败提示、明细弹窗都要它） */
  readonly previous: number;
  readonly requested: number;
  readonly at: number;
}

export interface ParamEnumValue {
  readonly value: number;
  readonly label: string;
}

export interface ParamRow {
  readonly name: string;
  readonly label: string;
  readonly value: number;
  readonly units: string | null;
  readonly description: string;
  /** 说明的来源：中文译表 / 上游英文 / 没有元数据。界面据此决定措辞，不猜 */
  readonly descriptionKind: 'zh' | 'en' | 'none';
  readonly group: string | null;
  readonly groupLabel: string;
  readonly readOnly: boolean;
  readonly rebootRequired: boolean;
  readonly range: { readonly low: number; readonly high: number } | null;
  readonly enumValues: readonly ParamEnumValue[] | null;
  readonly hasMetadata: boolean;
  readonly dangerous: boolean;
  readonly dangerReason: string | null;
  readonly pending: PendingEdit | null;
  /** 参考快照里的值；没有参考快照时为 null */
  readonly reference: number | null;
  readonly differsFromReference: boolean;
}

export interface ParamGroupOption {
  readonly id: string;
  readonly label: string;
  readonly count: number;
}

export interface ParamDownloadState {
  readonly phase: ParamDownloadPhase;
  readonly received: number;
  readonly total: number | null;
  readonly missing: number;
  readonly reason: string | null;
}

export interface ParamWriteFailure {
  readonly name: string;
  readonly status: ParamSetStatus;
  readonly requested: number;
  /** 回读到（或已知）的实际值；没等到回读时为 null。**值不符时界面必须显示它** */
  readonly actual: number | null;
  readonly message: string;
  readonly at: number;
}

export interface ParamWriteConfirmation {
  readonly name: string;
  readonly value: number;
  readonly at: number;
}

export interface ParamReference {
  readonly label: string;
  readonly values: ReadonlyMap<string, number>;
}

export interface ParamSessionSnapshot {
  readonly vehicleId: string;
  readonly rows: readonly ParamRow[];
  readonly total: number;
  readonly expected: number | null;
  readonly groups: readonly ParamGroupOption[];
  readonly metadata: ParamMetadataState;
  readonly download: ParamDownloadState | null;
  readonly lastDownload: ParamDownloadResult | null;
  readonly progress: ParamApplyProgress | null;
  /** 正在等待回读确认的参数名 */
  readonly writing: readonly string[];
  readonly failures: readonly ParamWriteFailure[];
  readonly confirmed: readonly ParamWriteConfirmation[];
  readonly pending: readonly PendingEdit[];
  readonly referenceLabel: string | null;
}

export interface CreateParameterSessionOptions {
  readonly vehicle: Vehicle;
  /** 初始元数据（注入式：测试与离线快照都走它） */
  readonly metadata?: ParamMetadataIndex | null;
  /** 元数据装载器（联网 + 缓存）。`null` = 这一份会话不去取元数据 */
  readonly metadataLoader?: ParamMetadataLoader | null;
  readonly managerOptions?: Omit<ParameterManagerOptions, 'vehicle' | 'metadata'>;
  readonly reference?: ParamReference | null;
  readonly now?: () => number;
}

/**
 * 失败与确认提示各留最近 50 条。
 *
 * 有上限是因为"一次写回 1500 条"里失败几百条是可能的：不限长会让这份列表跟着会话
 * 一直长，而界面上真正要看的是**最近**发生了什么（以及"还有几条没写进去"）。
 */
const FAILURE_LIMIT = 50;

export function describeSetStatus(status: ParamSetStatus): string {
  switch (status) {
    case 'value-mismatch':
      return '回读值不符';
    case 'no-response':
      return '没有回应';
    case 'read-only':
      return '只读参数';
    case 'invalid':
      return '取值非法';
    case 'send-failed':
      return '发送失败';
    case 'cancelled':
      return '已取消';
    case 'disposed':
      return '管理器已销毁';
    case 'applied':
      return '已确认';
  }
}

export class ParameterSession {
  readonly vehicle: Vehicle;
  readonly manager: ParameterManager;

  private readonly now: () => number;
  private readonly loader: ParamMetadataLoader | null;
  private readonly listeners = new Set<() => void>();
  private readonly offs: Unsubscribe[] = [];
  private readonly stopWatch: Unsubscribe;

  private readonly pendingEdits = new Map<string, PendingEdit>();
  private readonly writingNames = new Set<string>();
  private failures: ParamWriteFailure[] = [];
  private confirmed: ParamWriteConfirmation[] = [];

  private downloadState: ParamDownloadState | null = null;
  private lastDownload: ParamDownloadResult | null = null;
  private progress: ParamApplyProgress | null = null;

  private reference: ParamReference | null;
  private downloadPromise: Promise<ParamDownloadResult> | null = null;

  private snapshotCache: ParamSessionSnapshot | null = null;
  private disposed = false;

  constructor(options: CreateParameterSessionOptions) {
    this.vehicle = options.vehicle;
    this.now = options.now ?? (() => Date.now());
    this.loader = options.metadataLoader ?? null;
    this.reference = options.reference ?? null;

    const initialMetadata =
      options.metadata ?? this.loader?.index ?? createEmptyMetadataIndex('参数元数据尚未加载');

    this.manager = new ParameterManager({
      vehicle: options.vehicle,
      metadata: initialMetadata,
      ...(options.managerOptions ?? {}),
    });

    this.offs.push(
      this.manager.events.on('download', (event) => {
        this.downloadState = {
          phase: event.phase,
          received: event.received,
          total: event.total,
          missing: event.missing,
          reason: event.reason,
        };
        this.invalidate();
      }),
      this.manager.events.on('changed', (event) => {
        const pending = this.pendingEdits.get(event.name);
        /*
         * 机体侧的值已经等于我们想写的值时，这条改动就没有意义了：留着它会让界面
         * 一直显示"待写入"，而写进去也只会被 `apply()` 跳过。
         */
        if (pending !== undefined && paramValuesEqual(pending.requested, event.value)) {
          this.pendingEdits.delete(event.name);
        }
        this.invalidate();
      }),
      this.manager.events.on('apply', (event) => {
        this.progress = event;
        this.invalidate();
      }),
      this.manager.events.on('metadata', () => this.invalidate()),
    );

    // 页面开着的时候机体侧（或另一台地面站）改参数也要看得到
    this.stopWatch = this.manager.watch();
  }

  // -------------------------------------------------------------------------
  // React 边界
  // -------------------------------------------------------------------------

  /** 订阅会话变化。返回取消函数（**必须**在卸载时调用：忘记取消的表现是越用越慢） */
  readonly subscribe = (listener: () => void): Unsubscribe => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** 当前快照（引用稳定：没有变化时返回同一个对象） */
  getSnapshot(): ParamSessionSnapshot {
    this.snapshotCache ??= this.buildSnapshot();
    return this.snapshotCache;
  }

  /** 活跃订阅数（泄漏检查） */
  listenerCount(): number {
    return this.listeners.size;
  }

  private invalidate(): void {
    if (this.disposed) return;
    this.snapshotCache = null;
    for (const listener of [...this.listeners]) listener();
  }

  // -------------------------------------------------------------------------
  // 元数据
  // -------------------------------------------------------------------------

  get metadataState(): ParamMetadataState {
    if (this.loader !== null) return this.loader.state;
    const index = this.manager.paramMetadata;
    return {
      status: index?.source ?? null,
      size: index?.size ?? 0,
      reason: index?.unavailableReason ?? null,
      loading: false,
      url: null,
    };
  }

  /** 取元数据。失败不影响任何读写 —— 只是少说明、少范围提示 */
  async loadMetadata(force = false): Promise<void> {
    const loader = this.loader;
    if (loader === null) return;
    this.manager.setMetadata(await loader.load(force));
    this.invalidate();
  }

  setMetadata(index: ParamMetadataIndex | null): void {
    this.manager.setMetadata(index);
    this.invalidate();
  }

  // -------------------------------------------------------------------------
  // 读取
  // -------------------------------------------------------------------------

  rowOf(name: string): ParamRow | null {
    return this.getSnapshot().rows.find((row) => row.name === name) ?? null;
  }

  validate(name: string, value: number): ParamValidationResult {
    return this.manager.validate(name, value);
  }

  isDangerous(name: string): boolean {
    return isDangerousParam(name);
  }

  dangerReason(name: string): string | null {
    return dangerReasonOf(name);
  }

  setReference(reference: ParamReference | null): void {
    this.reference = reference;
    this.invalidate();
  }

  // -------------------------------------------------------------------------
  // 下载
  // -------------------------------------------------------------------------

  /**
   * 只在"这次会话还没下过"时下载。
   *
   * 参数页与摘要页会各调一次；管理器本身会把并发调用合并成一次下载，这里的记忆
   * 是为了避免"页签切回来就重新下一遍整张表"（1600 个参数在 57600 波特下要几十秒）。
   * 返回的是**那一次真实下载的结果**，不是"看起来成功了"的合成值。
   */
  ensureDownloaded(): Promise<ParamDownloadResult> {
    this.downloadPromise ??= this.refresh();
    return this.downloadPromise;
  }

  /** 强制重新下载（用户点"重新读取"时用） */
  reload(): Promise<ParamDownloadResult> {
    this.downloadPromise = this.refresh();
    return this.downloadPromise;
  }

  private async refresh(): Promise<ParamDownloadResult> {
    const result = await this.manager.download();
    this.lastDownload = result;
    this.downloadState = {
      phase: result.status === 'complete' ? 'complete' : result.status,
      received: result.received,
      total: result.total,
      missing: result.missing.length,
      reason: result.reason,
    };
    this.invalidate();
    return result;
  }

  cancelDownload(): void {
    this.manager.cancel();
  }

  // -------------------------------------------------------------------------
  // 待写入改动
  // -------------------------------------------------------------------------

  /**
   * 记录一条待写入改动。
   *
   * 拒绝的三种情况都必须**明说理由**：参数还没下载到、值不是有限数、元数据说它是只读。
   * 静默接受一条永远不会生效的改动，等于让用户在"我改过了"的错觉里继续操作。
   */
  setPending(name: string, value: number | null): { readonly ok: boolean; readonly reason: string | null } {
    const entry = this.manager.get(name);
    if (entry === undefined) {
      return { ok: false, reason: `参数表里没有 ${name}（可能还没下载完，或这一版固件没有它）` };
    }

    if (value === null) {
      this.pendingEdits.delete(name);
      this.invalidate();
      return { ok: true, reason: null };
    }

    if (!Number.isFinite(value)) {
      return { ok: false, reason: '参数值必须是有限数，已忽略这次输入' };
    }

    const readOnly = this.manager.paramMetadata?.get(name)?.readOnly === true;
    if (readOnly) {
      const reason = `${name} 在固件里是只读参数，写进去会被飞控忽略（未记录为待写入改动）`;
      this.noteRefusal(name, value, reason);
      return { ok: false, reason };
    }

    if (paramValuesEqual(entry.value, value)) {
      this.pendingEdits.delete(name);
      this.invalidate();
      return { ok: true, reason: null };
    }

    const edit: PendingEdit = Object.freeze({
      name,
      previous: entry.value,
      requested: value,
      at: this.now(),
    });
    this.pendingEdits.set(name, edit);
    // 重新改动之后，上一条针对它的失败提示就不再是当前事实了
    this.failures = this.failures.filter((failure) => failure.name !== name);
    this.invalidate();
    return { ok: true, reason: null };
  }

  /** 撤销一条（或全部）待写入改动 */
  revert(name: string): void {
    if (!this.pendingEdits.delete(name)) return;
    this.invalidate();
  }

  revertAll(): void {
    if (this.pendingEdits.size === 0) return;
    this.pendingEdits.clear();
    this.invalidate();
  }

  pendingDangerous(): readonly PendingEdit[] {
    return [...this.pendingEdits.values()].filter((edit) => isDangerousParam(edit.name));
  }

  dismissFailures(): void {
    if (this.failures.length === 0) return;
    this.failures = [];
    this.invalidate();
  }

  dismissFailure(name: string): void {
    const next = this.failures.filter((failure) => failure.name !== name);
    if (next.length === this.failures.length) return;
    this.failures = next;
    this.invalidate();
  }

  dismissConfirmations(): void {
    if (this.confirmed.length === 0) return;
    this.confirmed = [];
    this.invalidate();
  }

  // -------------------------------------------------------------------------
  // 写入
  // -------------------------------------------------------------------------

  /**
   * 写入**当前待写入的值**。没有待写入改动时返回 `null`（什么都没发）。
   *
   * 判据完全交给管理器：只有"收到同名回读且值在容差内相等"才算成功。这一层只负责
   * 把结论记账 —— 成功才清掉待写入改动并显示"已回读确认"；失败一律保留改动，
   * 因为那说明期望的状态**还没有**落到机体上。
   */
  async write(name: string): Promise<ParamSetResult | null> {
    const pending = this.pendingEdits.get(name);
    if (pending === undefined) return null;
    return this.writeValue(name, pending.requested, pending.previous);
  }

  /** 直接写一个值（元数据说只读时会被管理器拒绝，`PARAM_SET` 根本不会发出去） */
  async writeValue(name: string, value: number, previous?: number): Promise<ParamSetResult> {
    this.writingNames.add(name);
    this.invalidate();
    try {
      const result = await this.manager.set(name, value);
      this.noteWriteResult(result, previous ?? null);
      return result;
    } finally {
      this.writingNames.delete(name);
      this.invalidate();
    }
  }

  /**
   * 一次性写回所有待写入改动。
   *
   * 逐条等回读（见 `ParameterManager.apply`），因此进度是真实的而不是估算的。
   * 失败的那些**保留在待写入列表里** —— 界面上"还有几条没写进去"必须一直看得见。
   */
  async applyAll(): Promise<ParamApplyReport | null> {
    /*
     * 先**快照**待写入列表再发送。
     *
     * 不能边写边遍历 `pendingEdits`：写入成功时 `changed` 处理器会把这条改动从列表里删掉
     * （机体上已经是那个值了），于是"刚写成功的那些"会在遍历过程中消失 ——
     * 结果是写得越成功、报得越少。
     */
    const edits = [...this.pendingEdits.values()];
    const entries = edits.map((edit) => ({ name: edit.name, value: edit.requested }));
    if (entries.length === 0) return null;

    for (const edit of edits) this.writingNames.add(edit.name);
    this.invalidate();

    try {
      const report = await this.manager.apply(entries, { skipUnchanged: true });
      const byName = new Map(report.results.map((result) => [result.name, result]));

      for (const edit of edits) {
        const result = byName.get(edit.name);
        if (result === undefined) {
          // 管理器判定"与当前值相同"而跳过：机体上已经是这个值了，改动可以收掉
          this.pendingEdits.delete(edit.name);
          continue;
        }
        this.noteWriteResult(result, edit.previous);
      }
      return report;
    } finally {
      this.writingNames.clear();
      this.invalidate();
    }
  }

  private noteWriteResult(result: ParamSetResult, previous: number | null): void {
    const name = result.name;
    if (result.status === 'applied') {
      this.pendingEdits.delete(name);
      this.failures = this.failures.filter((failure) => failure.name !== name);
      const value = result.actual ?? result.requested;
      this.confirmed = [
        ...this.confirmed.filter((item) => item.name !== name).slice(-(FAILURE_LIMIT - 1)),
        { name, value, at: this.now() },
      ];
      return;
    }

    // 失败：待写入改动留在列表里，用户才知道"还没写成"
    if (previous !== null && !this.pendingEdits.has(name)) {
      this.pendingEdits.set(
        name,
        Object.freeze({ name, previous, requested: result.requested, at: this.now() }),
      );
    }
    const failure: ParamWriteFailure = {
      name,
      status: result.status,
      requested: result.requested,
      actual: result.actual,
      message: result.message ?? `${describeSetStatus(result.status)}（没有更多信息）`,
      at: this.now(),
    };
    this.failures = [...this.failures.filter((item) => item.name !== name), failure].slice(
      -FAILURE_LIMIT,
    );
  }

  /** 只读被拒时的记账：界面上要有一条"为什么没写"的明确记录 */
  private noteRefusal(name: string, value: number, reason: string): void {
    const failure: ParamWriteFailure = {
      name,
      status: 'read-only',
      requested: value,
      actual: this.manager.get(name)?.value ?? null,
      message: reason,
      at: this.now(),
    };
    this.failures = [...this.failures.filter((item) => item.name !== name), failure].slice(
      -FAILURE_LIMIT,
    );
    this.invalidate();
  }

  // -------------------------------------------------------------------------
  // 快照
  // -------------------------------------------------------------------------

  private buildSnapshot(): ParamSessionSnapshot {
    const items = this.manager.list();
    const rows = items.map((item) => this.buildRow(item));
    return {
      vehicleId: this.vehicle.id,
      rows,
      total: items.length,
      expected: this.manager.expectedCount,
      groups: buildGroups(rows),
      metadata: this.metadataState,
      download: this.downloadState,
      lastDownload: this.lastDownload,
      progress: this.progress,
      writing: [...this.writingNames].sort(),
      failures: Object.freeze([...this.failures]),
      confirmed: Object.freeze([...this.confirmed]),
      pending: Object.freeze([...this.pendingEdits.values()]),
      referenceLabel: this.reference?.label ?? null,
    };
  }

  private buildRow(item: ParamListItem): ParamRow {
    const referenceValue = this.reference?.values.get(item.name) ?? null;
    return {
      name: item.name,
      label: item.label,
      value: item.value,
      units: item.units,
      description: item.descriptionZh ?? item.description ?? '',
      descriptionKind:
        item.descriptionZh !== null ? 'zh' : item.description !== null ? 'en' : 'none',
      group: item.group,
      groupLabel: item.groupLabel ?? '无元数据',
      readOnly: item.readOnly,
      rebootRequired: item.rebootRequired,
      range: item.metadata?.range ?? null,
      enumValues: enumValuesOf(item),
      hasMetadata: item.metadata !== null,
      dangerous: isDangerousParam(item.name),
      dangerReason: dangerReasonOf(item.name),
      pending: this.pendingEdits.get(item.name) ?? null,
      reference: referenceValue,
      differsFromReference:
        referenceValue !== null && !paramValuesEqual(referenceValue, item.value),
    };
  }

  // -------------------------------------------------------------------------
  // 生命周期
  // -------------------------------------------------------------------------

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const off of this.offs) off();
    this.offs.length = 0;
    this.listeners.clear();
    this.stopWatch();
    this.loader?.dispose();
    this.manager.dispose();
    this.snapshotCache = null;
  }
}

function enumValuesOf(item: ParamListItem): readonly ParamEnumValue[] | null {
  const values = item.metadata?.values;
  if (values === null || values === undefined) return null;
  return [...values.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.value - b.value);
}

/**
 * 分组列表由**当前这批参数**聚合，而不是直接抄元数据的分组表。
 *
 * 两个原因：元数据里可能有这一版固件没有的分组（点了筛出来是空的），
 * 而且参数表在元数据缺失时一样要能按分组看 —— 那时分组名来自参数本身。
 */
function buildGroups(rows: readonly ParamRow[]): readonly ParamGroupOption[] {
  const counts = new Map<string, { label: string; count: number }>();
  for (const row of rows) {
    const id = row.group ?? '';
    const current = counts.get(id);
    if (current === undefined) counts.set(id, { label: row.groupLabel, count: 1 });
    else current.count += 1;
  }
  return [...counts.entries()]
    .map(([id, value]) => ({ id, label: value.label, count: value.count }))
    .sort((a, b) => (a.count === b.count ? a.label.localeCompare(b.label, 'zh-Hans-CN') : b.count - a.count));
}

export function createParameterSession(options: CreateParameterSessionOptions): ParameterSession {
  return new ParameterSession(options);
}
